import { Input, PageHeader } from '@egw/components'
import { useTranslation } from '@egw/i18n'
import { formatInputToCurrency } from '@egw/utils'
import { type FC } from 'react'
import { Controller, FormProvider } from 'react-hook-form'
import { type FeatureFlags, WEALTH_ROUTES } from '@egw/constants'
import { useFlags } from '@egw/feature-flags'
import {
  HelperText,
  IraInfoBlock,
  SelectDropdownIcon,
  SubmitTransferButton,
  TransferFormWrapper,
  TransferLimitsSection,
} from '../../components'
import { TransferOptionsDrawer } from '../../components/transfer-options-drawer/transfer-options-drawer'
import { TransferPageContext, useTransferContextProvider } from '../../context'
import { TransferField } from '../../types'
import { addCents } from '../../utils'

export const BankTransfer: FC = () => {
  return (
    <TransferPageContext>
      <TransferPageComponent />
    </TransferPageContext>
  )
}

const TransferPageComponent: FC = () => {
  const { t } = useTranslation('features-transfer')
  const { flags } = useFlags<FeatureFlags>()
  const {
    isAccountDataError,
    formMethods,
    fromFieldOption,
    fromFieldDisplayValue,
    toFieldOption,
    toFieldDisplayValue,
    openFromModal,
    openToModal,
  } = useTransferContextProvider()

  return (
    <>
      <PageHeader
        backLinkUrl={WEALTH_ROUTES.TRANSFER_BANK.backPath}
        title={t('transfer.deposit_or_withdrawal_title')}
        subtitle={t('transfer.title')}
      />
      <div className="egw-container pt-0">
        <TransferFormWrapper>
          <FormProvider {...formMethods}>
            <form className="flex w-full flex-col gap-4" id="transfer" method="post">
              <Input
                id={TransferField.From}
                value={fromFieldDisplayValue ?? ''}
                onFocus={openFromModal}
                disabled={isAccountDataError}
                label={t('transfer.from')}
                readOnly
                icon={<SelectDropdownIcon isDisabled={isAccountDataError} onClick={openFromModal} />}
              />
              <Controller
                name={TransferField.From}
                control={formMethods.control}
                render={({ field }) => <input type="hidden" {...field} value={field.value as unknown as string} />}
              />
              <HelperText />

              <Input
                id={TransferField.To}
                value={toFieldDisplayValue ?? ''}
                onFocus={openToModal}
                disabled={isAccountDataError || !fromFieldOption}
                error={formMethods.formState.errors[TransferField.To]?.message}
                label={t('transfer.to')}
                readOnly
                icon={<SelectDropdownIcon isDisabled={isAccountDataError || !fromFieldOption} onClick={openToModal} />}
              />

              <Controller
                name={TransferField.To}
                control={formMethods.control}
                render={({ field }) => <input type="hidden" {...field} value={field.value as unknown as string} />}
              />

              <Controller
                name={TransferField.Amount}
                control={formMethods.control}
                render={({ field }) => (
                  <Input
                    {...field}
                    id={TransferField.Amount}
                    label={t('transfer.amount')}
                    valueFormatter={(value) => formatInputToCurrency(value, true)}
                    onBlur={() => {
                      addCents({ formMethods })
                    }}
                    disabled={isAccountDataError || !toFieldOption}
                    inputMode="decimal"
                    error={formMethods.formState.errors[TransferField.Amount]?.message}
                  />
                )}
              />

              {
                /* Show Transfer Limit Popover(s) when From Field is Filled and ACH Order */
                <TransferLimitsSection />
              }
              {
                /* Show IRA Withdrawal | Deposit Info Block when From | To Field is ROTH_IRA or TRADITIONAL_IRA */
                <IraInfoBlock />
              }
              {
                /** From / To Options Modal */
                <TransferOptionsDrawer />
              }
              <SubmitTransferButton />
            </form>
          </FormProvider>
        </TransferFormWrapper>
      </div>
    </>
  )
}
